/**
 * Swiss Ephemeris layer — TypeScript replacement for pyswisseph (`import swisseph as swe`).
 *
 * Package choice: `sweph-wasm` v2.6.9 (AGPL-3.0-or-later, https://github.com/ptprashanttripathi/sweph-wasm)
 * — Swiss Ephemeris 2.10.03 compiled to WebAssembly, the SAME version the original Python
 * reference engine's pyswisseph wrapped, fed the SAME `.se1` data files (now in `public/ephe/`).
 * Validation against the golden fixtures dumped from that reference engine (`fixtures/`)
 * agrees to ≤ 4e-8 deg for planet positions/speeds, ≤ 6e-14 deg for house cusps, and
 * exactly (0 deviation) for ayanamsa and sunrise times (see src/tests/ephemeris.test.ts).
 * Rationale over alternatives:
 *   - `swisseph` / `sweph` (native N-API): Node-only, cannot run in a browser Web Worker.
 *   - `swisseph-wasm` (prolaxu): v0.0.x, thinner API, ships a fixed preloaded .data bundle.
 *   - `sweph-wasm` exposes the full raw `swe_*` surface we need (calc_ut, houses_ex,
 *     rise_trans, set_sid_mode, get_ayanamsa_ut) plus the emscripten runtime (FS, _malloc,
 *     stringToUTF8), which lets us load OUR `.se1` files into the WASM filesystem in both
 *     Node and the browser instead of fetching the package's own copies over the network.
 *
 * Environment handling (see `initEphemeris`):
 *   - The package's own `SwissEPH.init()` / `swe_set_ephe_path()` use `fetch()` and fail
 *     under Node (no file:// fetch), so we bypass both: we obtain the `.wasm` binary and
 *     `.se1` bytes through a pluggable {@link EphemerisFileProvider} and write the data
 *     files into the emscripten FS ourselves, then call the raw `_swe_set_ephe_path`.
 *   - Browser/worker default provider: `fetch(import.meta.env.BASE_URL + "ephe/" + name)`
 *     — the files live in `public/ephe/` (the `.se1` data files plus the package's
 *     `swisseph.wasm`).
 *   - Node (vitest) default provider: `node:fs` reads `public/ephe/`
 *     directly, and resolves `swisseph.wasm` out of `node_modules/sweph-wasm`. The fs
 *     import is dynamic and guarded by `typeof process` so nothing Node-only leaks into
 *     the browser bundle.
 *
 * API mirrors pyswisseph names and tuple shapes so the chart port can translate 1:1
 * (`import * as swe from "./ephemeris"` then `swe.calc_ut(...)` like Python's `swe.calc_ut`).
 * Differences from pyswisseph the porter must know:
 *   - Everything throws until `initEphemeris()` has resolved (call it once at startup).
 *   - `calc_ut` returns `[xx, retflags]` like Python, but `retflags` is the *requested*
 *     flags value (the WASM wrapper does not surface the effective flags; every Python
 *     call site discards it: `res, _ = swe.calc_ut(...)`).
 *   - `houses_ex(jd, lat, lon, hsys, flags)` keeps the PYTHON argument order (the
 *     underlying wasm call is `swe_houses_ex(jd, iflag, lat, lon, hsys)`), takes `hsys`
 *     as a 1-char string ('P'/'W' — Python passes `b'P'`), and returns
 *     `[cusps, ascmc]` with `cusps` a 12-element array (cusp 1 first, like pyswisseph —
 *     the C 13-slot array's unused [0] is stripped) and `ascmc` 8 elements
 *     `[asc, mc, armc, vertex, equasc, coasc1, coasc2, polasc]`. Throws on failure
 *     (e.g. Placidus at polar latitudes), exactly where pyswisseph raises — callers
 *     keep the Python try/fallback-to-'W' structure.
 *   - `rise_trans(jd, body, rsmi, geopos)` returns `(res, tret)` like Python:
 *     `res` 0 = found (tret[0] = JD UT of the event), -2 = circumpolar (never
 *     rises/sets); genuine errors throw, matching pyswisseph raising swisseph.Error.
 *   - `julday(y, m, d, ut_hours)` always uses the Gregorian calendar (pyswisseph's
 *     default `GREG_CAL`); the engine rejects pre-1600 dates upstream anyway.
 *   - Angles are degrees, distances AU, speeds deg/day — identical to pyswisseph.
 */

import SwissEPH from "sweph-wasm";

// ---------------------------------------------------------------------------
// Constants — values are Swiss Ephemeris canon (verified against pyswisseph at
// fixture-generation time). Exported under pyswisseph names (swe.SUN, ...) and,
// for discoverability, under the C-style SE_ aliases.
// ---------------------------------------------------------------------------

export const SUN = 0;
export const MOON = 1;
export const MERCURY = 2;
export const VENUS = 3;
export const MARS = 4;
export const JUPITER = 5;
export const SATURN = 6;
export const URANUS = 7;
export const NEPTUNE = 8;
export const PLUTO = 9;
export const MEAN_NODE = 10;
export const TRUE_NODE = 11;
export const CHIRON = 15;

export const SE_SUN = SUN;
export const SE_MOON = MOON;
export const SE_MERCURY = MERCURY;
export const SE_VENUS = VENUS;
export const SE_MARS = MARS;
export const SE_JUPITER = JUPITER;
export const SE_SATURN = SATURN;
export const SE_URANUS = URANUS;
export const SE_NEPTUNE = NEPTUNE;
export const SE_PLUTO = PLUTO;
export const SE_MEAN_NODE = MEAN_NODE;
export const SE_TRUE_NODE = TRUE_NODE;
export const SE_CHIRON = CHIRON;

/** Use Swiss Ephemeris .se1 data files (pyswisseph FLG_SWIEPH, the default ephemeris). */
export const FLG_SWIEPH = 2;
export const FLG_SPEED = 256;
export const FLG_SIDEREAL = 64 * 1024;

export const SIDM_LAHIRI = 1;

export const CALC_RISE = 1;
export const CALC_SET = 2; // Swiss Ephemeris SE_CALC_SET (setting event; rsmi passed through to swe_rise_trans)
export const BIT_DISC_CENTER = 256;
export const BIT_NO_REFRACTION = 512;

export const GREG_CAL = 1;

// ---------------------------------------------------------------------------
// Initialisation / file loading
// ---------------------------------------------------------------------------

/**
 * Resolves the raw bytes of an ephemeris asset by file name. Names requested:
 * the entries of {@link EPHE_FILES} plus {@link WASM_FILE} ("swisseph.wasm").
 */
export type EphemerisFileProvider = (name: string) => Promise<Uint8Array>;

/**
 * The Swiss Ephemeris data files shipped in `public/ephe/`. The `_18` files cover
 * 1800-2399 AD — the whole range this app accepts; the `m18` files (1800-1201 BC)
 * are shipped for completeness and are never read in practice.
 */
export const EPHE_FILES = [
  "seas_18.se1",
  "seasm18.se1",
  "semo_18.se1",
  "semom18.se1",
  "sepl_18.se1",
  "seplm18.se1",
] as const;

/** The emscripten WASM binary of sweph-wasm (copied to public/ephe/ for the browser). */
export const WASM_FILE = "swisseph.wasm";

/** Path of the .se1 directory inside the emscripten virtual filesystem. */
const FS_EPHE_DIR = "/ephe";

/** Where the .se1 files live on disk when running under Node (vitest): the same
 * static assets the browser serves from public/ephe/. */
const NODE_EPHE_DIR = "public/ephe";

let _swe: SwissEPH | null = null;
let _initPromise: Promise<void> | null = null;

async function default_provider(name: string): Promise<Uint8Array> {
  if (typeof process !== "undefined" && process.versions?.node) {
    // Node (vitest). Dynamic imports keep node:* out of the browser bundle;
    // @vite-ignore stops Vite/Rollup from trying to resolve them at build time
    // (this branch can never execute in the browser).
    const { readFile } = await import(/* @vite-ignore */ "node:fs/promises");
    const { join, dirname } = await import(/* @vite-ignore */ "node:path");
    if (name === WASM_FILE) {
      const { createRequire } = await import(/* @vite-ignore */ "node:module");
      const require = createRequire(import.meta.url);
      const glue = require.resolve("sweph-wasm/wasm/swisseph");
      return new Uint8Array(await readFile(join(dirname(glue), WASM_FILE)));
    }
    return new Uint8Array(await readFile(join(process.cwd(), NODE_EPHE_DIR, name)));
  }
  // Browser / Web Worker: static assets under public/ephe/.
  const url = `${import.meta.env.BASE_URL}ephe/${name}`;
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`Failed to fetch ephemeris file ${url}: HTTP ${resp.status}`);
  }
  return new Uint8Array(await resp.arrayBuffer());
}

/**
 * Load the Swiss Ephemeris WASM module, write the .se1 data files into its
 * virtual filesystem, set the ephemeris path, and set the Lahiri sidereal mode
 * — the TS equivalent of `_chart.ensure_swiss_ephemeris()`:
 *
 *     swe.set_ephe_path(str(ephe_path))
 *     swe.set_sid_mode(swe.SIDM_LAHIRI)
 *
 * Idempotent and safe to race: concurrent callers share one in-flight promise.
 * Every other export of this module throws until this has resolved once.
 */
export async function initEphemeris(provider: EphemerisFileProvider = default_provider): Promise<void> {
  if (_swe) return;
  if (!_initPromise) {
    _initPromise = (async () => {
      const [wasmBinary, ...epheBytes] = await Promise.all([
        provider(WASM_FILE),
        ...EPHE_FILES.map((name) => provider(name)),
      ]);
      // Bypass SwissEPH.init(): it locates the .wasm via fetch(), which fails in
      // Node. Supplying wasmBinary skips emscripten's own file resolution entirely.
      // (Normalize to an exact-size ArrayBuffer in case a provider returns a view.)
      const exact =
        wasmBinary.byteOffset === 0 && wasmBinary.byteLength === wasmBinary.buffer.byteLength
          ? wasmBinary
          : wasmBinary.slice();
      const factory = (await import("sweph-wasm/wasm/swisseph")).default;
      const wasm = await factory({ wasmBinary: exact.buffer as ArrayBuffer });
      const swe = new SwissEPH(wasm);

      // Bypass swe.swe_set_ephe_path() for the same reason (it fetches): write
      // our own .se1 bytes into the emscripten FS and point the C library at them.
      wasm.FS.mkdir(FS_EPHE_DIR);
      EPHE_FILES.forEach((name, i) => {
        wasm.FS.writeFile(`${FS_EPHE_DIR}/${name}`, epheBytes[i]);
      });
      const n = wasm.lengthBytesUTF8(FS_EPHE_DIR) + 1;
      const ptr = wasm._malloc(n);
      wasm.stringToUTF8(FS_EPHE_DIR, ptr, n);
      wasm._swe_set_ephe_path(ptr);
      wasm._free(ptr);

      swe.swe_set_sid_mode(SIDM_LAHIRI, 0, 0);
      _swe = swe;
    })();
  }
  try {
    await _initPromise;
  } catch (err) {
    _initPromise = null; // allow a retry after a transient failure (e.g. fetch error)
    throw err;
  }
}

/** True once {@link initEphemeris} has completed. */
export function isInitialized(): boolean {
  return _swe !== null;
}

function require_swe(): SwissEPH {
  if (!_swe) {
    throw new Error(
      "Swiss Ephemeris not initialised — call (and await) initEphemeris() before using ephemeris functions."
    );
  }
  return _swe;
}

// ---------------------------------------------------------------------------
// pyswisseph-shaped API
// ---------------------------------------------------------------------------

/**
 * Julian Day (UT) from a Gregorian calendar date and decimal UT hours.
 * Mirrors `swe.julday(y, m, d, ut_hours)` (Gregorian flag implied, as in pyswisseph).
 */
export function julday(year: number, month: number, day: number, ut_hours: number): number {
  return require_swe().swe_julday(year, month, day, ut_hours, GREG_CAL);
}

/**
 * Mirrors `swe.set_sid_mode(swe.SIDM_LAHIRI)`. Lahiri is already set by
 * {@link initEphemeris}; exposed so `ensure_swiss_ephemeris` ports 1:1.
 */
export function set_sid_mode(sid_mode: number, t0 = 0, ayan_t0 = 0): void {
  require_swe().swe_set_sid_mode(sid_mode, t0, ayan_t0);
}

/**
 * Mirrors `swe.set_ephe_path(path)` as a no-op shim: the real path is fixed to
 * the WASM virtual filesystem by {@link initEphemeris}; this only asserts that
 * initialisation has happened so `ensure_swiss_ephemeris` ports 1:1.
 */
export function set_ephe_path(_path?: string): void {
  require_swe();
}

/** Ayanamsa value (degrees) for a JD(UT) under the sidereal mode set at init (Lahiri). */
export function get_ayanamsa_ut(jd_ut: number): number {
  return require_swe().swe_get_ayanamsa_ut(jd_ut);
}

/**
 * Mirrors `res, retflags = swe.calc_ut(jd, body, flags)`.
 * `res` is `[lon, lat, dist, lon_speed, lat_speed, dist_speed]`
 * (sidereal degrees when FLG_SIDEREAL is set; speeds populated when FLG_SPEED is set).
 * `retflags` here is the requested `flags` value — see module header. Throws on error.
 */
export function calc_ut(jd_ut: number, body: number, flags: number): [number[], number] {
  const res = require_swe().swe_calc_ut(jd_ut, body, flags);
  return [Array.from(res, Number), flags];
}

/**
 * Mirrors `cusps, ascmc = swe.houses_ex(jd, lat, lon, hsys, flags)` — Python
 * argument order, `hsys` as 'P' (Placidus) or 'W' (Whole sign).
 * Returns `[cusps, ascmc]`: `cusps[0..11]` = house cusps 1..12 (sidereal degrees
 * when FLG_SIDEREAL is set), `ascmc[0]` = Ascendant, `ascmc[1]` = MC (then armc,
 * vertex, equasc, coasc1, coasc2, polasc).
 * THROWS where pyswisseph raises — e.g. Placidus at polar latitudes — so the
 * Python try/except fallback to 'W' translates directly to try/catch.
 */
export function houses_ex(
  jd_ut: number,
  lat: number,
  lon: number,
  hsys: string,
  flags: number
): [number[], number[]] {
  const { cusps, ascmc } = require_swe().swe_houses_ex(
    jd_ut,
    flags,
    lat,
    lon,
    hsys as Parameters<SwissEPH["swe_houses_ex"]>[4]
  );
  // The wasm wrapper returns the C 13-slot cusp array (index 0 unused);
  // pyswisseph returns a 12-tuple starting at cusp 1 — match pyswisseph.
  return [Array.from(cusps, Number).slice(1, 13), Array.from(ascmc, Number)];
}

/**
 * Mirrors `res, tret = swe.rise_trans(jd_ut, body, rsmi, geopos)`.
 * `geopos` is `(lon, lat, elevation_m)` — LONGITUDE FIRST, as in the C API and
 * the Python call site (_kp.py:246). Returns `[res, tret]` with `res` 0 = event
 * found (`tret[0]` = JD UT), -2 = circumpolar (Sun never rises/sets; tret stays 0,
 * exactly what pyswisseph returns). Genuine calculation errors throw, matching
 * pyswisseph raising `swisseph.Error` (the Python caller catches and returns None).
 */
export function rise_trans(
  jd_ut: number,
  body: number,
  rsmi: number,
  geopos: readonly [number, number, number]
): [number, number[]] {
  const swe = require_swe();
  const tret = new Array(10).fill(0);
  try {
    const t = swe.swe_rise_trans(
      jd_ut,
      body,
      null,
      FLG_SWIEPH,
      rsmi,
      [geopos[0], geopos[1], geopos[2]],
      0,
      0
    );
    tret[0] = t;
    return [0, tret];
  } catch (err) {
    // sweph-wasm throws SweError for every retcode < 0; pyswisseph returns
    // (-2, zeros) for circumpolar and only raises for real errors (-1).
    if (err && typeof err === "object" && (err as { flag?: number }).flag === -2) {
      return [-2, tret];
    }
    throw err;
  }
}

/** Swiss Ephemeris library version string (e.g. "2.10.03"). */
export function version(): string {
  return require_swe().swe_version();
}
